/**
 * The authoring assistant: a request in, an edited floor and an honest
 * account of it out.
 *
 * THE RULE THIS FILE KEEPS. Every sentence the author reads is written from
 * two things that are true after the edit: the diff the edit returned, and
 * the product route run over the result (`flow/coverage.ts`). Nothing here
 * describes what a fab "would be like". Delete CMP-11 and the reply does not
 * say "you will struggle with planarisation" - it says thirty operations
 * have nowhere to run and the damascene loop is broken, because that is what
 * the route came back with, and it would say something different tomorrow if
 * the route changed. It is the same commitment the tool assistants make
 * about physics, applied to a floor plan.
 *
 * WHAT THE MODEL DOES AND DOES NOT DO. It picks one of nine actions. It does
 * not pick the tool - `match.ts` does that against the translated catalogue -
 * and it does not write the reply. So the worst a bad model call can do is
 * perform the wrong KIND of edit, which the author can see and undo, and it
 * can never produce a floor plan that is described as something it is not.
 */

import { t, tOr } from '../../i18n/i18n'
import type { FloorPlan } from '../../fab/floorPlan'
import {
  coverageFraction,
  coverageOf,
  segmentComplete,
  type FloorCoverage,
  type SegmentCoverage,
} from '../../sim/flow/coverage'
import {
  AUTHOR_ACTIONS,
  exampleOf,
  isQuestion,
  rankActionsLocally,
  summaryOf,
  type AuthorActionId,
} from './actions'
import { archetype } from './archetypes'
import {
  idOfTag,
  idsInZone,
  labelOf,
  processOf,
  STATIONS,
  stationIds,
  tagOf,
  ZONES,
  type FlowZone,
} from './catalogue'
import {
  addTools,
  addZone,
  clearTools,
  didNothing,
  removeTools,
  removeZone,
  renamePlan,
  setPlanHall,
  sortToFlow,
  useArchetype,
  type Change,
} from './edits'
import {
  matchAll,
  matchArchetype,
  matchHall,
  matchName,
  matchTools,
  matchZoneAt,
  tagList,
  withoutPhrase,
} from './match'
import { routeAuthorAction } from './route'

export interface AuthorReply {
  /** The floor after the edit. The same plan when nothing changed. */
  plan: FloorPlan
  /** What happened, and what it means for the product route. */
  lines: string[]
  action: AuthorActionId | null
  /** True when this device routed the request rather than a model. */
  local: boolean
  /** The floor's coverage after the edit, for the readout beside the chat. */
  coverage: FloorCoverage
  /** Offered when nothing was done and something was close. */
  offer?: { action: AuthorActionId; example: string }
}

/** The tags standing on a floor, which is what coverage is measured over. */
export const tagsOf = (plan: FloorPlan): string[] => plan.tools.map(tagOf)

export const coverageForPlan = (plan: FloorPlan): FloorCoverage => coverageOf(tagsOf(plan))

const pct = (c: FloorCoverage): number => Math.round(coverageFraction(c) * 100)

const segmentName = (s: SegmentCoverage): string => tOr(`author.segment.${s.id}`, s.id)

const list = (items: string[]): string => items.join(tOr('author.ListSeparator', ', '))

/**
 * What the product route makes of this floor.
 *
 * Four sentences at most, in the order that matters to somebody deciding
 * what to add next: how much of the route runs, which stretches run end to
 * end, and the single tool whose absence costs the most operations. The last
 * one is the useful one - it turns "78%" into "add ECD-12".
 */
export function describeCoverage(c: FloorCoverage, opts: { segments?: boolean } = {}): string[] {
  const lines: string[] = []
  if (c.steps === 0) return lines
  if (c.complete) {
    lines.push(t('author.reply.Complete', { steps: c.steps, masks: c.masks }))
  } else if (c.covered === 0) {
    lines.push(t('author.reply.CoverageNone', { steps: c.steps }))
  } else {
    lines.push(t('author.reply.Coverage', { covered: c.covered, steps: c.steps, percent: pct(c) }))
  }

  const whole = c.segments.filter(segmentComplete)
  if (opts.segments !== false && whole.length && !c.complete) {
    lines.push(t('author.reply.SegmentsRun', { segments: list(whole.map(segmentName)) }))
  }

  const worst = c.gaps[0]
  if (worst) {
    const id = idOfTag(worst.tag)
    lines.push(
      t('author.reply.BiggestGap', {
        tag: worst.tag,
        process: id ? processOf(id) : worst.tag,
        // i18next pluralises on a variable named `count`, and this is the
        // one sentence here whose number genuinely reaches one.
        count: worst.steps,
      }),
    )
  }
  return lines
}

/**
 * The stretch of route an edit opened or broke.
 *
 * The most useful thing to say after "removed CMP-11" is not the new
 * percentage, it is that the metal loop stopped running - the consequence,
 * named, at the moment it happens. Comparing the two coverages is the only
 * way to know that, so both are computed and only the difference is spoken.
 */
function segmentDelta(before: FloorCoverage, after: FloorCoverage): string[] {
  const was = new Map(before.segments.map((s) => [s.id, segmentComplete(s)]))
  const opened: string[] = []
  const broken: string[] = []
  for (const s of after.segments) {
    const now = segmentComplete(s)
    if (now === was.get(s.id)) continue
    ;(now ? opened : broken).push(segmentName(s))
  }
  const lines: string[] = []
  if (opened.length) lines.push(t('author.reply.SegmentsOpened', { segments: list(opened) }))
  if (broken.length) lines.push(t('author.reply.SegmentsBroken', { segments: list(broken) }))
  return lines
}

/** What an edit did, in the author's language, from the diff and nothing else. */
function describeChange(c: Change, action: AuthorActionId): string[] {
  const lines: string[] = []
  if (c.added.length) lines.push(t('author.reply.Added', { tags: tagList(c.added) }))
  if (c.removed.length) lines.push(t('author.reply.Removed', { tags: tagList(c.removed) }))
  if (c.refused.length) lines.push(t('author.reply.Refused', { tags: tagList(c.refused) }))
  if (c.unchanged.length) {
    lines.push(
      t(action === 'remove' ? 'author.reply.NotThere' : 'author.reply.AlreadyThere', {
        tags: tagList(c.unchanged),
      }),
    )
  }
  if (c.hallChanged) {
    lines.push(t('author.reply.Hall', { hall: tOr(`author.hall.${c.plan.hall}`, c.plan.hall) }))
  }
  if (c.renamed) lines.push(t('author.reply.Renamed', { name: c.plan.name }))
  if (c.reordered) lines.push(t('author.reply.Sorted'))
  return lines
}

/**
 * What a request is about: the stations it names, and the stage of the flow
 * it names, with each read so the other cannot swallow it.
 *
 * The zone's own words are struck out before the stations are looked for,
 * because the two vocabularies overlap on purpose - "interconnect" is a
 * stage of the flow AND the line under ECD-12's asset tag. Strike it and
 * "add the interconnect" asks for the stage, while "add an etcher to the
 * front end" still asks for the etcher.
 */
function subjects(question: string): { zone: FlowZone | null; tools: string[] } {
  const named = matchZoneAt(question)
  const rest = named ? withoutPhrase(question, named.phrase) : question
  return { zone: named?.zone ?? null, tools: matchTools(rest) }
}

/**
 * Perform one action. Returns the change, which may be a no-op - that is a
 * result, not a failure, and the reply says so.
 */
function perform(plan: FloorPlan, action: AuthorActionId, question: string): Change {
  switch (action) {
    case 'add': {
      const { zone, tools } = subjects(question)
      if (tools.length) return addTools(plan, tools)
      if (zone) return addZone(plan, zone)
      // "Add everything": the whole catalogue, which is the IDM floor by
      // another name but asked for as an edit rather than a fresh start.
      return matchAll(question) ? addTools(plan, stationIds()) : addTools(plan, [])
    }
    case 'remove': {
      const { zone, tools } = subjects(question)
      if (tools.length) return removeTools(plan, tools)
      if (zone) return removeZone(plan, zone)
      // "Remove everything" is a clear, spelled the other way; it goes
      // through removeTools so the airlock is refused the same way.
      return matchAll(question) ? removeTools(plan, stationIds()) : removeTools(plan, [])
    }
    case 'archetype': {
      const id = matchArchetype(question)
      return id ? useArchetype(plan, id) : addTools(plan, [])
    }
    case 'hall': {
      const hall = matchHall(question)
      return hall ? setPlanHall(plan, hall) : addTools(plan, [])
    }
    case 'rename': {
      const name = matchName(question)
      return name ? renamePlan(plan, name) : addTools(plan, [])
    }
    case 'sort':
      return sortToFlow(plan)
    case 'clear':
      return clearTools(plan)
    case 'assess':
    case 'catalogue':
      return addTools(plan, [])
  }
}

/** "There are 22 stations: 4 in the front end, of which you have 3…" */
function describeCatalogue(plan: FloorPlan): string[] {
  const on = new Set(plan.tools)
  const lines = [t('author.reply.CatalogueHead', { n: STATIONS.length, chosen: plan.tools.length })]
  for (const zone of ZONES) {
    const ids = idsInZone(zone)
    if (!ids.length) continue
    lines.push(
      t('author.reply.CatalogueZone', {
        zone: tOr(`author.zone.${zone}`, zone),
        tools: list(ids.map((id) => (on.has(id) ? `${tagOf(id)} ✓` : tagOf(id)))),
      }),
    )
  }
  return lines
}

/** The floor as it stands, for a request that only asked. */
function describeFloor(plan: FloorPlan, c: FloorCoverage): string[] {
  const lines: string[] = []
  if (plan.tools.length <= 1) {
    lines.push(t('author.reply.EmptyFloor'))
    return lines
  }
  lines.push(t('author.reply.Floor', { n: plan.tools.length, tools: tagList(plan.tools) }))
  lines.push(...describeCoverage(c))
  // The half of the building that is not a process tool. Absent, it is the
  // most consequential thing about a floor plan and the least visible.
  const missingHouse = ['facilities', 'gas'].filter((id) => !plan.tools.includes(id))
  if (missingHouse.length) {
    lines.push(t('author.reply.NoHouse', { tags: tagList(missingHouse) }))
  }
  if (c.unused.length) lines.push(t('author.reply.Unused', { tags: list(c.unused) }))
  return lines
}

/** What this assistant can be asked, for a request it could not place. */
export function whatCanIAsk(): string[] {
  return AUTHOR_ACTIONS.filter((id) => !isQuestion(id)).map((id) => `${summaryOf(id)} — "${exampleOf(id)}"`)
}

/**
 * Ask the assistant to change a floor.
 *
 * Async only because the routing may go over the network; every edit and
 * every sentence is computed here, synchronously, from the plan it was
 * handed. The plan is never saved by this function - the dialog owns that,
 * so an author can see what a request did before keeping it.
 */
export async function askAuthor(plan: FloorPlan, question: string): Promise<AuthorReply> {
  const before = coverageForPlan(plan)
  const routing = await routeAuthorAction(question)

  if (!routing.id) {
    const nearest = routing.nearest
    return {
      plan,
      action: null,
      local: routing.local,
      coverage: before,
      lines: [t('author.reply.Unplaced'), ...whatCanIAsk()],
      offer: nearest ? { action: nearest, example: exampleOf(nearest) } : undefined,
    }
  }

  /**
   * THE MODEL'S ANSWER FIRST, THEN THIS DEVICE'S RANKING.
   *
   * Verbs overlap - "put it in flow order" leads with a word that means
   * add, and there is nothing in the sentence to add - so a single choice
   * would fail on a request anybody would call clear. Each candidate is
   * performed against a COPY of the plan and kept only if it did something;
   * the first that can act wins, and if none can, the first is what gets
   * reported, because that is the reading the author most likely meant.
   */
  const candidates: AuthorActionId[] = []
  for (const id of [routing.id, ...rankActionsLocally(question)]) {
    if (id && !candidates.includes(id)) candidates.push(id)
  }

  /**
   * NOTHING THAT REPLACES A FLOOR RUNS ON A SENTENCE THAT NAMES A TOOL.
   *
   * The two actions that throw work away - a fresh archetype, a cleared
   * floor - act only as the FIRST reading of a request, and only when the
   * request names no station. "Remove CMP from the foundry" leads, in
   * Chinese, with a word that means "from", which is an archetype verb;
   * it also names CMP-11, and a sentence that names a tool is a sentence
   * about that tool. Falling through to either is barred outright, because
   * that is how "add the wipe-down station" would end up clearing a fab.
   */
  const destructive = (id: AuthorActionId): boolean => id === 'clear' || id === 'archetype'
  const namesATool = subjects(question).tools.length > 0
  if (destructive(candidates[0]) && namesATool && candidates.length > 1) candidates.shift()

  let chosen: AuthorActionId = candidates[0]
  let change = perform(plan, chosen, question)
  if (!isQuestion(chosen) && didNothing(change)) {
    for (const id of candidates.slice(1)) {
      if (destructive(id)) continue
      const attempt = perform(plan, id, question)
      if (isQuestion(id) || !didNothing(attempt)) {
        chosen = id
        change = attempt
        break
      }
    }
  }

  // The archetype it already is. A different sentence from "nothing in
  // that sentence told me what to do it to", because the sentence did.
  if (chosen === 'archetype' && didNothing(change)) {
    const id = matchArchetype(question)
    if (id && archetype(id)) {
      return {
        plan,
        action: chosen,
        local: routing.local,
        coverage: before,
        lines: [t('author.reply.SameArchetype', { name: tOr(`author.archetype.${id}.Name`, id) })],
      }
    }
  }

  if (chosen === 'catalogue') {
    return { plan, action: chosen, local: routing.local, coverage: before, lines: describeCatalogue(plan) }
  }
  if (chosen === 'assess') {
    return { plan, action: chosen, local: routing.local, coverage: before, lines: describeFloor(plan, before) }
  }

  if (didNothing(change)) {
    // Routed, but the request named nothing this floor could act on - an
    // "add" with no tool in it, a hall that is already the hall. Saying
    // which is more use than saying "sorry".
    const lines = describeChange(change, chosen)
    return {
      plan,
      action: chosen,
      local: routing.local,
      coverage: before,
      lines: lines.length ? lines : [t('author.reply.NothingToDo', { summary: summaryOf(chosen) })],
      offer: lines.length ? undefined : { action: chosen, example: exampleOf(chosen) },
    }
  }

  const after = coverageForPlan(change.plan)
  const lines = describeChange(change, chosen)
  if (chosen === 'archetype') {
    const id = matchArchetype(question)
    if (id && archetype(id)) {
      lines.unshift(t('author.reply.Archetype', { name: tOr(`author.archetype.${id}.Name`, id) }))
    }
  }
  lines.push(...segmentDelta(before, after))
  // The headline number, once, after the consequences rather than before
  // them: the author asked for a change, not for a score.
  if (change.added.length || change.removed.length) {
    lines.push(
      after.complete
        ? t('author.reply.Complete', { steps: after.steps, masks: after.masks })
        : t('author.reply.Coverage', { covered: after.covered, steps: after.steps, percent: pct(after) }),
    )
  }
  return { plan: change.plan, action: chosen, local: routing.local, coverage: after, lines }
}

/** A tool's label, re-exported so the dialog and the assistant agree on it. */
export { labelOf }
